import { useEffect, useState, useRef } from 'react';

/**
 * Detects if content overflows its container by comparing scrollHeight vs clientHeight.
 * The container should have overflow:hidden and a constrained height.
 * Automatically re-checks on resize.
 */
export const useContentOverflow = () => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const checkOverflow = () => {
      const { scrollHeight, clientHeight } = container;
      setIsOverflowing(scrollHeight > clientHeight);
    };

    // Use requestAnimationFrame to ensure measurement happens after browser layout/paint.
    // This guarantees DOM is fully rendered and dimensions are accurate before checking overflow.
    requestAnimationFrame(checkOverflow);

    const resizeObserver = new ResizeObserver(() => {
      // Also wait for next frame after resize to ensure layout has been recalculated.
      requestAnimationFrame(checkOverflow);
    });
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  return { containerRef, isOverflowing };
};
